import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiHome,
  FiMapPin,
  FiMenu,
  FiUserPlus,
  FiBriefcase,
  FiUsers,
} from '../../utils/icons';
import { useSidebar } from '../../contexts/SidebarContext';

const SidebarContainer = styled.aside<{ $isCollapsed: boolean }>`
  width: 200px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.divider};
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 200;
  overflow-y: auto;
  padding: 12px 8px;
  transform: translateX(${({ $isCollapsed }) => ($isCollapsed ? '-100%' : '0')});
  transition: transform ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    transform: translateX(${({ $isCollapsed }) => ($isCollapsed ? '-100%' : '0')});
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  margin-bottom: 16px;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    font-size: 20px;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;

    svg {
      color: white;
    }
  }

  svg {
    font-size: 18px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SectionTitle = styled.div`
  padding: 16px 12px 8px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 8px;
`;

export const Sidebar: React.FC = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();

  return (
    <SidebarContainer $isCollapsed={isCollapsed}>
      <MenuButton onClick={toggleSidebar} title="Toggle sidebar">
        <FiMenu />
      </MenuButton>
      <Nav>
        <StyledNavLink to="/" end>
          <FiHome />
          Dashboard
        </StyledNavLink>
        <StyledNavLink to="/map">
          <FiMapPin />
          Map
        </StyledNavLink>

        <SectionTitle>Management</SectionTitle>
        <StyledNavLink to="/organizations">
          <FiBriefcase />
          Organizations
        </StyledNavLink>
        <StyledNavLink to="/locations">
          <FiMapPin />
          Locations
        </StyledNavLink>

        <SectionTitle>Employees</SectionTitle>
        <StyledNavLink to="/employees">
          <FiUsers />
          All Employees
        </StyledNavLink>
        <StyledNavLink to="/onboarding">
          <FiUserPlus />
          Onboarding
        </StyledNavLink>
      </Nav>
    </SidebarContainer>
  );
};
